import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import { Feature, getGroupDisplayColor, PlanInterval } from '../../../models';
import getFeaturePrefix = Feature.getFeaturePrefix;
import { useLaunchDarkly } from '../../../launchdarkly';
import { SubscriptionPlanPrice } from '../../../models/subscriptionPlanPrice';
import { useNavigate, useQuery } from '../../../routes';
import { plan } from '../../../layout/routes';
import { ContactSaleBtn, ProductBox } from '../../../components';
import { useAddLicenseHandler } from '../hooks/useAddLicenseHandler';

const DEFAULT_PRODUCT_QUOTA = 1;

export const ProductAddMoreBox = (
  { feature, selfServe }: {
    feature: Feature,
    selfServe?: boolean,
  },
) => {
  const intl = useIntl();
  const { flags } = useLaunchDarkly();
  const { queryDictionary } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const groupId = Feature.getFeatureGroup(feature);
  const color = getGroupDisplayColor(groupId);
  const prefix = getFeaturePrefix((feature));

  // Use custom hook for add license logic
  const {
    handleAddLicense,
    AwsMarketplaceDialogComponent,
  } = useAddLicenseHandler();

  const navigateToPlan = () => {
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [`${prefix}Quota`]: DEFAULT_PRODUCT_QUOTA,
        [`${prefix}Cycle`]: PlanInterval.YEAR,
      }),
    );
  };

  const goToPlan = () => {
    handleAddLicense(navigateToPlan);
  };

  return (
    <ProductBox
      key={`add-more-${feature}`}
      id={`add-more-${feature}`}
    >
      <Box
        id={`add-more-${feature}-header`}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          fontSize={14}
          color={color ?? '#727993'}
          textTransform="uppercase"
        >
          <FormattedMessage id={`product.group.${groupId}.title`} />
        </Typography>
      </Box>
      <Box
        id={`subscription-type-${feature.toLowerCase()}`}
        key={`subscription-type-${feature.toLowerCase()}`}
        display="flex"
        flexDirection="column"
        width="100%"
      >
        <Box
          display="flex"
          flexDirection="column"
        >
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={theme => ({
              minHeight: theme.spacing(8),
            })}
          >
            <FormattedMessage
              id={`feature.${feature.toLowerCase()}.product.name`}
            />
          </Typography>
          <Typography
            variant="body2"
            fontWeight={400}
            fontSize={16}
            sx={theme => ({
              paddingBottom: theme.spacing(1),
              minHeight: theme.spacing(8),
            })}
          >
            <FormattedMessage
              id={`feature.${feature.toLowerCase()}.product.desc`}
            />
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          paddingTop={1}
          height={theme => theme.spacing(12)}
        >
          <Typography
            fontWeight={700}
            fontSize={18}
            paddingBottom={1}
          >
            <FormattedMessage
              id="page.subscription.add.more.pricing.title"
            />
          </Typography>
          {selfServe
            ? (
              <>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  fontSize={14}
                >
                  <FormattedMessage
                    id="page.subscription.add.more.pricing.self.serve"
                    values={{
                      cycle: (
                        <strong>{PlanInterval.getPlanIntervalName(PlanInterval.YEAR)}</strong>
                      ),
                      price: flags?.subscriptionPlanPrice?.[`${prefix}Annually` as keyof SubscriptionPlanPrice]?.toLocaleString('en-us'),
                      priceUnit: intl.formatMessage({ id: `feature.${feature.toLowerCase()}.unit` }),
                      cycleUnit: PlanInterval.getCycle(PlanInterval.YEAR),
                    }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  fontSize={14}
                >
                  <FormattedMessage
                    id="page.subscription.add.more.pricing.self.serve"
                    values={{
                      cycle: (
                        <strong>{ PlanInterval.getPlanIntervalName(PlanInterval.MONTH)}</strong>
                      ),
                      price: flags?.subscriptionPlanPrice?.[`${prefix}Monthly` as keyof SubscriptionPlanPrice],
                      priceUnit: intl.formatMessage({ id: `feature.${feature.toLowerCase()}.unit` }),
                      cycleUnit: PlanInterval.getCycle(PlanInterval.MONTH),
                    }}
                  />
                </Typography>
              </>
            )
            : (
              <Typography
                variant="body2"
                fontWeight={400}
                fontSize={14}
              >
                <FormattedMessage id="page.subscription.add.more.pricing.direct" />
              </Typography>
            )}
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        justifySelf="self-end"
        paddingTop={2}
      >
        {selfServe
          ? (
            <Button
              variant="contained"
              size="small"
              onClick={goToPlan}
              sx={{
                color: '#FFFFFF',
                backgroundColor: '#5959EB',
                '&:hover': {
                  backgroundColor: '#43509B',
                },
              }}
            >
              <FormattedMessage
                id="page.subscription.current.subscription.manage.add.to.plan"
              />
            </Button>
          )
          : (
            <ContactSaleBtn fullWidth />
          )}
      </Box>
      <AwsMarketplaceDialogComponent />
    </ProductBox>
  );
};
